import React, { FunctionComponent, useState, useRef } from 'react';

// Custom
import DialogContext from '@/ui/DialogContext/DialogContext';
import ConfirmDialog, { ConfirmDialogOptions } from '@/ui/Dialog/ConfirmDialog/ConfirmDialog';

// Types
import { IDialogProps, IDialogProvider, DialogProviderState } from '@/@types/ui/DialogContext';

export type ConfirmResult = {
  confirmed?: boolean;
  canceled?: boolean;
  error?: boolean;
};

const DialogProvider: FunctionComponent<IDialogProvider> = (props) => {
  const { children } = props;

  const [providerState, setProviderState] = useState<DialogProviderState>({
    component1: null,
    props1: {},
    component2: null,
    props2: {},
    component3: null,
    props3: {}
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogOptions, setConfirmDialogOptions] = useState<ConfirmDialogOptions | null>(null);

  const awaitingPromiseRef = useRef<{
    resolve: (r: ConfirmResult) => void;
    reject: (r: ConfirmResult) => void;
  }>();

  const showConfirmDialog = (options: ConfirmDialogOptions) => {
    setConfirmDialogOptions(options);
    setConfirmDialogOpen(true);
    return new Promise<ConfirmResult>((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject };
    });
  };

  const handleConfirmClose = () => {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve({ canceled: true });
      //can also be .reject but then we have to catch this in useFormController
    }

    setConfirmDialogOpen(false);
  };

  const handleConfirmSubmit = () => {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve({ confirmed: true });
    }

    setConfirmDialogOpen(false);
  };
  const showDialog = (newComponent: FunctionComponent, newProps = {}, level = 1) => {
    if (level === 1) {
      setProviderState({
        component1: newComponent,
        props1: newProps,
        component2: null,
        props2: {},
        component3: null,
        props3: {}
      });
    } else if (level === 2) {
      setProviderState((prevState) => {
        return {
          component1: prevState?.component1,
          props1: prevState?.props1,
          component2: newComponent,
          props2: newProps,
          component3: null,
          props3: {}
        };
      });
    } else if (level === 3) {
      setProviderState((prevState) => {
        return {
          component1: prevState?.component1,
          props1: prevState?.props1,
          component2: prevState?.component2,
          props2: prevState?.props2,
          component3: newComponent,
          props3: newProps
        };
      });
    }
  };

  const hideDialog = (level = 1) => {
    if (level === 1) {
      setProviderState((prevState) => {
        return {
          component1: null,
          props1: {},
          component2: null,
          props2: {},
          component3: null,
          props3: {}
        };
      });
    } else if (level === 2) {
      setProviderState((prevState) => {
        return {
          component1: prevState.component1,
          props1: prevState.props1,
          component2: null,
          props2: {},
          component3: null,
          props3: {}
        };
      });
    } else if (level === 3) {
      setProviderState((prevState) => {
        return {
          component1: prevState.component1,
          props1: prevState.props1,
          component2: prevState.component2,
          props2: prevState.props2,
          component3: null,
          props3: {}
        };
      });
    }
  };

  // Add this method to update props of an active dialog
  const updateDialogProps = (level: number, newProps: IDialogProps) => {

    if (level === 1) {
      setProviderState((prevState) => ({
        ...prevState,
        props1: { ...prevState.props1, ...newProps },
      }));
    } else if (level === 2) {
      setProviderState((prevState) => ({
        ...prevState,
        props2: { ...prevState.props2, ...newProps },
      }));
    } else if (level === 3) {
      setProviderState((prevState) => ({
        ...prevState,
        props3: { ...prevState.props3, ...newProps },
      }));

    }
  };

  const providerValue = {
    component1: providerState?.component1,
    component2: providerState?.component2,
    component3: providerState?.component3,
    props1: providerState?.props1,
    props2: providerState?.props2,
    props3: providerState?.props3,
    showDialog,
    hideDialog,
    showConfirmDialog,
    updateDialogProps
  };

  return (
    <>
      <DialogContext.Provider value={providerValue} children={children} />
      <ConfirmDialog open={confirmDialogOpen} onSubmit={handleConfirmSubmit} onCancel={handleConfirmClose} {...confirmDialogOptions} />
    </>
  );
};

export default DialogProvider;
